import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FileText, Linkedin, MessageSquare, Target, Briefcase, Users, CheckCircle2, ArrowLeft, ArrowRight, Star } from 'lucide-react';
import { SEO } from '../components/SEO';
interface PageProps {
  openGetStarted?: () => void;
}
const SERVICES_DATA: Record<string, any> = {
  resume: {
    icon: <FileText size={48} data-id="element-452" />,
    title: 'Professional Resume Writing',
    subtitle: 'Pass the ATS and impress human recruiters.',
    desc: 'A powerful, ATS-optimized resume tailored to your specific industry and career goals. We highlight your achievements, not just your duties.',
    benefits: ['Keyword optimization for your target roles', 'Modern, clean, ATS-friendly formatting', 'Achievement-based bullet points', 'Direct collaboration with a certified writer'],
    process: ['Upload your current resume or fill out a questionnaire.', 'Consult with your dedicated writer.', 'Receive your first draft in 3-5 business days.', 'Revise until you are 100% satisfied.'],
    levels: [{
      name: 'Essential',
      price: 149,
      blurb: 'Entry-level & early-career',
      turnaround: '5-day delivery',
      highlight: false
    }, {
      name: 'Professional',
      price: 349,
      blurb: 'Mid-level professionals',
      turnaround: '3-day delivery',
      highlight: true
    }, {
      name: 'Premium',
      price: 549,
      blurb: 'Senior & specialized roles',
      turnaround: '48-hour delivery',
      highlight: false
    }]
  },
  linkedin: {
    icon: <Linkedin size={48} data-id="element-453" />,
    title: 'LinkedIn Profile Optimization',
    subtitle: 'Turn your profile into a recruiter magnet.',
    desc: 'Transform your LinkedIn profile to increase your visibility in search results and attract inbound opportunities.',
    benefits: ['Keyword-rich headline and summary', 'Optimized experience and skills sections', 'Custom background banner recommendation', 'Networking and job search strategy guide'],
    process: ['Share your target roles and industries.', 'We analyze your current profile and competitors.', 'Receive a comprehensive optimization document.', 'Easily copy and paste the updates to your profile.'],
    levels: [{
      name: 'Basic',
      price: 99,
      blurb: 'Headline & summary refresh',
      turnaround: '4-day delivery',
      highlight: false
    }, {
      name: 'Standard',
      price: 149,
      blurb: 'Full profile optimization',
      turnaround: '3-day delivery',
      highlight: true
    }, {
      name: 'Premium',
      price: 219,
      blurb: 'Profile + content strategy',
      turnaround: '48-hour delivery',
      highlight: false
    }]
  },
  'cover-letter': {
    icon: <MessageSquare size={48} data-id="element-454" />,
    title: 'Custom Cover Letters',
    subtitle: 'Tell the story your resume cannot.',
    desc: 'A persuasive narrative that connects your experience to the specific needs of the employer, showing them exactly why you are the perfect fit.',
    benefits: ['Targeted messaging for specific roles', 'Compelling opening hooks', 'Clear value proposition', 'Strong call to action'],
    process: ['Provide a target job description.', 'We craft a narrative aligning your skills to the role.', 'Receive a highly customizable template.', 'Adapt easily for future applications.'],
    levels: [{
      name: 'Single',
      price: 79,
      blurb: 'One tailored cover letter',
      turnaround: '3-day delivery',
      highlight: false
    }, {
      name: 'Pair',
      price: 129,
      blurb: 'Two role-specific letters',
      turnaround: '3-day delivery',
      highlight: true
    }, {
      name: 'Bundle',
      price: 169,
      blurb: 'Letter + thank-you templates',
      turnaround: '48-hour delivery',
      highlight: false
    }]
  },
  executive: {
    icon: <Briefcase size={48} data-id="element-455" />,
    title: 'Executive Branding',
    subtitle: 'Strategic positioning for senior leaders.',
    desc: 'Comprehensive career marketing for Directors, VPs, and C-Suite executives looking to position themselves as industry leaders.',
    benefits: ['Executive summary and value proposition', 'Board-ready formatting and tone', 'Leadership and ROI highlighting', 'Executive bio document included'],
    process: ['In-depth 1-on-1 strategy consultation.', 'Comprehensive career history review.', 'Drafting of executive resume and bio.', 'Unlimited revisions until perfect.'],
    levels: [{
      name: 'Director',
      price: 399,
      blurb: 'Director & senior manager',
      turnaround: '3-day delivery',
      highlight: false
    }, {
      name: 'VP',
      price: 549,
      blurb: 'Vice President level',
      turnaround: '48-hour delivery',
      highlight: true
    }, {
      name: 'C-Suite',
      price: 749,
      blurb: 'C-Suite & board roles',
      turnaround: '48-hour delivery',
      highlight: false
    }]
  },
  coaching: {
    icon: <Target size={48} data-id="element-456" />,
    title: 'Interview Coaching',
    subtitle: 'Master the interview and negotiate with confidence.',
    desc: 'One-on-one sessions to help you master the STAR method, handle difficult questions, and negotiate your salary with confidence.',
    benefits: ['Mock interviews with real-time feedback', 'STAR method mastery', 'Salary negotiation strategies', 'Confidence building techniques'],
    process: ['Schedule your 60-minute video session.', 'Provide target job descriptions.', 'Conduct a realistic mock interview.', 'Receive a detailed feedback report and action plan.'],
    levels: [{
      name: 'Single Session',
      price: 129,
      blurb: 'One 60-min coaching call',
      turnaround: 'Book within 48 hours',
      highlight: false
    }, {
      name: '3-Session Pack',
      price: 329,
      blurb: 'Three coaching sessions',
      turnaround: 'Flexible scheduling',
      highlight: true
    }, {
      name: 'Intensive',
      price: 549,
      blurb: 'Five sessions + materials',
      turnaround: 'Priority scheduling',
      highlight: false
    }]
  },
  career: {
    icon: <Users size={48} data-id="element-457" />,
    title: 'Career Transition',
    subtitle: 'Pivot to a new industry seamlessly.',
    desc: 'Specialized support for professionals looking to change industries or roles. We help translate your transferable skills effectively.',
    benefits: ['Transferable skills translation', 'New industry targeting strategy', 'Narrative shift and gap explanation', 'Rebranding across all documents'],
    process: ['Deep-dive consultation on your transition goals.', 'Identify key transferable skills.', 'Rewrite resume and LinkedIn for the new target.', 'Develop a transition networking strategy.'],
    levels: [{
      name: 'Starter',
      price: 199,
      blurb: 'Resume repositioning',
      turnaround: '5-day delivery',
      highlight: false
    }, {
      name: 'Complete',
      price: 349,
      blurb: 'Resume + LinkedIn + strategy',
      turnaround: '3-day delivery',
      highlight: true
    }, {
      name: 'Premium',
      price: 499,
      blurb: 'Full rebrand + coaching call',
      turnaround: '48-hour delivery',
      highlight: false
    }]
  }
};
export function ServiceDetail({
  openGetStarted
}: PageProps) {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const service = id ? SERVICES_DATA[id] : null;
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);
  if (!service) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4" data-id="element-458">
        <h1 className="text-3xl font-heading font-bold text-navy-900 dark:text-white mb-4" data-id="element-459">
          Service Not Found
        </h1>
        <button onClick={() => navigate('/services')} className="text-brand hover:underline flex items-center gap-2" data-id="element-460">
          <ArrowLeft size={16} data-id="element-461" /> Back to Services
        </button>
      </div>;
  }
  return <div className="w-full pt-20 pb-24" data-id="element-462">
      <SEO title={`${service.title} | Hirelio Career Services`} description={service.desc} data-id="element-463" />

      {/* Hero Section with 3D effect */}
      <div className="bg-navy-900 text-white py-20 lg:py-32 relative overflow-hidden perspective-1000" data-id="element-464">
        <div className="absolute inset-0 bg-grid opacity-20" data-id="element-465"></div>
        <div className="absolute top-0 right-0 -mr-40 -mt-40 w-96 h-96 rounded-full bg-brand-500/20 blur-3xl opacity-50 z-0" data-id="element-466"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-467">
          <Link to="/services" className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-8 text-sm font-medium" data-id="element-468">
            <ArrowLeft size={16} data-id="element-469" /> Back to all services
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-470">
            <div className="animate-in slide-in-from-bottom-8 duration-700" data-id="element-471">
              <div className="w-20 h-20 bg-brand-500/20 rounded-2xl flex items-center justify-center text-brand-400 mb-8 border border-brand-500/30 shadow-[0_0_30px_rgba(37,99,235,0.3)]" data-id="element-472">
                {service.icon}
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-6 leading-tight" data-id="element-473">
                {service.title}
              </h1>
              <p className="text-xl text-brand-200 mb-4 font-medium" data-id="element-474">
                {service.subtitle}
              </p>
              <p className="text-lg text-slate-300 mb-10 leading-relaxed max-w-xl" data-id="element-475">
                {service.desc}
              </p>
              <Link to="/contact" className="inline-flex bg-brand hover:bg-brand-600 text-white px-8 py-4 rounded-xl font-medium text-lg transition-all shadow-[0_10px_20px_rgba(37,99,235,0.3)] hover:shadow-[0_15px_30px_rgba(37,99,235,0.4)] hover:-translate-y-1 items-center gap-2" data-id="element-476">
                Get Started Now <ArrowRight size={20} data-id="element-477" />
              </Link>
            </div>

            {/* 3D Floating Element */}
            <div className="hidden lg:block relative perspective-1000" data-id="element-478">
              <div className="relative w-full aspect-square max-w-md mx-auto transform-gpu rotate-y-[-10deg] rotate-x-[5deg] hover:rotate-y-0 hover:rotate-x-0 transition-transform duration-700 ease-out" data-id="element-479">
                <div className="absolute inset-0 bg-gradient-to-tr from-brand-600 to-emerald-400 rounded-3xl opacity-20 blur-2xl" data-id="element-480"></div>
                <div className="relative h-full bg-navy-800 border border-navy-700 rounded-3xl p-8 shadow-2xl flex flex-col justify-between overflow-hidden" data-id="element-481">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-brand-500/10 rounded-bl-full" data-id="element-482"></div>
                  <div data-id="element-483">
                    <div className="flex items-center gap-3 mb-8" data-id="element-484">
                      <div className="w-12 h-12 rounded-full bg-navy-700 flex items-center justify-center" data-id="element-485">
                        <Star className="text-amber-400" size={20} data-id="element-486" />
                      </div>
                      <div data-id="element-487">
                        <div className="text-sm text-slate-400" data-id="element-488">
                          Client Success
                        </div>
                        <div className="font-bold text-white" data-id="element-489">
                          Interview Landed
                        </div>
                      </div>
                    </div>
                    <div className="space-y-4" data-id="element-490">
                      <div className="h-4 bg-navy-700 rounded w-3/4" data-id="element-491"></div>
                      <div className="h-4 bg-navy-700 rounded w-full" data-id="element-492"></div>
                      <div className="h-4 bg-navy-700 rounded w-5/6" data-id="element-493"></div>
                    </div>
                  </div>
                  <div className="mt-8 p-4 bg-navy-900/50 rounded-xl border border-navy-700 backdrop-blur-sm" data-id="element-494">
                    <div className="flex items-center gap-3" data-id="element-495">
                      <CheckCircle2 className="text-emerald-400" size={24} data-id="element-496" />
                      <span className="font-medium text-emerald-50" data-id="element-497">
                        ATS Optimized & Ready
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-id="element-498">
        <div className="grid md:grid-cols-2 gap-16" data-id="element-499">
          {/* Benefits */}
          <div data-id="element-500">
            <h2 className="text-3xl font-heading font-bold text-navy-900 dark:text-white mb-8" data-id="element-501">
              What's Included
            </h2>
            <div className="space-y-6" data-id="element-502">
              {service.benefits.map((benefit: string, idx: number) => <div key={idx} className="flex items-start gap-4 p-6 bg-white dark:bg-navy-800 rounded-2xl border border-slate-100 dark:border-navy-700 shadow-sm hover:shadow-md transition-shadow" data-id="element-503">
                  <div className="w-10 h-10 rounded-full bg-brand-50 dark:bg-navy-900 flex items-center justify-center text-brand shrink-0" data-id="element-504">
                    <CheckCircle2 size={20} data-id="element-505" />
                  </div>
                  <div data-id="element-506">
                    <p className="font-medium text-navy-900 dark:text-white text-lg" data-id="element-507">
                      {benefit}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Process */}
          <div data-id="element-508">
            <h2 className="text-3xl font-heading font-bold text-navy-900 dark:text-white mb-8" data-id="element-509">
              How It Works
            </h2>
            <div className="relative border-l-2 border-slate-200 dark:border-navy-700 ml-5 space-y-10" data-id="element-510">
              {service.process.map((step: string, idx: number) => <div key={idx} className="relative pl-8" data-id="element-511">
                  <div className="absolute -left-[17px] top-1 w-8 h-8 rounded-full bg-brand text-white flex items-center justify-center font-bold text-sm shadow-md" data-id="element-512">
                    {idx + 1}
                  </div>
                  <h3 className="text-xl font-bold text-navy-900 dark:text-white mb-2" data-id="element-513">
                    Step {idx + 1}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 leading-relaxed" data-id="element-514">
                    {step}
                  </p>
                </div>)}
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center" data-id="element-515">
        <div className="bg-slate-50 dark:bg-navy-800/50 rounded-3xl p-10 md:p-16 border border-slate-200 dark:border-navy-700" data-id="element-516">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy-900 dark:text-white mb-6" data-id="element-517">
            Ready to elevate your career?
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 mb-10 max-w-2xl mx-auto" data-id="element-518">
            Take the next step with our {service.title.toLowerCase()} service
            and start landing more interviews.
          </p>
          <Link to="/contact" className="inline-flex bg-brand hover:bg-brand-600 text-white px-8 py-4 rounded-xl font-medium text-lg transition-all shadow-lg shadow-brand/25 items-center justify-center gap-2 mx-auto" data-id="element-519">
            Get Started Now <ArrowRight size={20} data-id="element-520" />
          </Link>
        </div>
      </div>
    </div>;
}